/**
 * SettingsPage.jsx - VERSION CORRIGÉE
 *
 * ✅ FIX 1: Double requête (une sans auth) supprimée
 * ✅ FIX 2: Couleur des Alert en dark mode fixée
 */

import React, { useState, useEffect } from "react";
import {
    Card,
    Form,
    Input,
    Button,
    Space,
    message,
    Spin,
    Alert,
    Divider,
    Modal,
    Tooltip,
} from "antd";
import {
    SaveOutlined,
    DeleteOutlined,
    LinkOutlined,
    LockOutlined,
    UserOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import {
    getUserSettings,
    updateUserSettings,
    getDiscordAuthorizationUrl,
    deleteUserAccount,
} from "../api/api";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

/**
 * SettingsPage - Paramètres utilisateur (FIXED)
 */
export default function SettingsPage() {
    const { isDarkMode } = useTheme();
    const { logout, token } = useAuth();  // ✅ Récupère le token
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        login: "",
        discord_user_id: "",
    });

    // ✅ FIX 1: Récupère les settings SEULEMENT si token existe
    useEffect(() => {
        // Si pas de token, redirige vers login
        if (!token) {
            navigate("/login");
            return;
        }

        const fetchSettings = async () => {
            try {
                setLoading(true);
                const data = await getUserSettings();
                setSettings({
                    login: data.login,
                    discord_user_id: data.discord_user_id || "",
                });

                // Pré-remplit le form
                form.setFieldsValue({
                    login: data.login,
                    discord_user_id: data.discord_user_id || "",
                });
            } catch (err) {
                console.error(err);
                message.error("Erreur chargement settings");
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, [form, token, navigate]);  // ✅ Ajoute token comme dépendance

    /**
     * Sauvegarde les settings
     */
    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            const payload = {};

            // Login changé
            if (values.login && values.login !== settings.login) {
                payload.login = values.login.trim();
            }

            // Password fourni
            if (values.password) {
                if (!values.password_confirm) {
                    message.error("Confirme ton mot de passe");
                    return;
                }
                payload.password = values.password;
                payload.password_confirm = values.password_confirm;
            }

            // Discord changé
            if (values.discord_user_id !== settings.discord_user_id) {
                payload.discord_user_id = values.discord_user_id || null;
            }

            // Rien à sauvegarder
            if (Object.keys(payload).length === 0) {
                message.warning("Aucun changement");
                return;
            }

            setSaving(true);
            const res = await updateUserSettings(payload);

            setSettings({
                login: res.login,
                discord_user_id: res.discord_user_id || "",
            });

            // Reset form
            form.setFieldsValue({
                login: res.login,
                discord_user_id: res.discord_user_id || "",
                password: "",
                password_confirm: "",
            });

            message.success("✅ Paramètres mis à jour !");
        } catch (err) {
            console.error(err);
            message.error(err.response?.data?.error || err.message || "Erreur sauvegarde");
        } finally {
            setSaving(false);
        }
    };

    /**
     * OAuth Discord
     */
    const handleDiscordOAuth = async () => {
        try {
            setSaving(true);
            const res = await getDiscordAuthorizationUrl();

            const popup = window.open(
                res.authorization_url,
                "Discord OAuth",
                "width=500,height=600"
            );

            const checkPopup = setInterval(() => {
                if (popup?.closed) {
                    clearInterval(checkPopup);
                    // Recharge les settings
                    setSaving(true);
                    getUserSettings()
                        .then((data) => {
                            setSettings({
                                login: data.login,
                                discord_user_id: data.discord_user_id || "",
                            });
                            form.setFieldsValue({
                                discord_user_id: data.discord_user_id || "",
                            });
                            message.success("✅ Discord connecté !");
                        })
                        .catch(() => message.error("Erreur Discord"))
                        .finally(() => setSaving(false));
                }
            }, 1000);
        } catch (err) {
            console.error(err);
            message.error("Erreur Discord OAuth");
            setSaving(false);
        }
    };

    /**
     * Déconnecter Discord
     */
    const handleRemoveDiscord = async () => {
        Modal.confirm({
            title: "Déconnecter Discord ?",
            content: "Les notifications Discord seront désactivées",
            okText: "Oui",
            cancelText: "Non",
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    setSaving(true);
                    await updateUserSettings({ discord_user_id: "" });

                    setSettings({ ...settings, discord_user_id: "" });
                    form.setFieldsValue({ discord_user_id: "" });
                    message.success("Discord déconnecté");
                } catch (err) {
                    message.error("Erreur");
                } finally {
                    setSaving(false);
                }
            },
        });
    };

    /**
     * Supprimer le compte
     */
    const handleDeleteAccount = () => {
        Modal.confirm({
            title: "⚠️ Supprimer ton compte ?",
            content: (
                <div style={{ color: isDarkMode ? "#ffffff" : "#000000" }}>
                    <p>
                        <strong>Cette action est irréversible !</strong>
                    </p>
                    <p>Toutes tes données seront supprimées :</p>
                    <ul>
                        <li>❌ Compte utilisateur</li>
                        <li>❌ Recherches sauvegardées</li>
                        <li>❌ Alertes</li>
                        <li>❌ Favoris</li>
                    </ul>
                    <p style={{ color: "#ff4d4f", fontWeight: "600" }}>
                        Confirme en cliquant "Supprimer"
                    </p>
                </div>
            ),
            okText: "Supprimer",
            cancelText: "Annuler",
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    setSaving(true);
                    await deleteUserAccount();
                    message.success("Compte supprimé");
                    logout();
                    navigate("/");
                } catch (err) {
                    message.error("Erreur suppression");
                } finally {
                    setSaving(false);
                }
            },
        });
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: "48px" }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div
            style={{
                padding: "24px",
                minHeight: "100vh",
                background: isDarkMode ? "#000000" : "#fafafa",
            }}
        >
            <div style={{ maxWidth: "700px", margin: "0 auto" }}>
                {/* Header */}
                <div style={{ marginBottom: "32px" }}>
                    <h1 style={{ fontSize: "28px", fontWeight: "700", margin: "0 0 8px" }}>
                        ⚙️ Paramètres
                    </h1>
                    <p style={{ opacity: 0.6, margin: 0 }}>
                        Gère ton compte et tes notifications
                    </p>
                </div>

                <Form form={form} layout="vertical" onFinish={handleSave}>
                    {/* ============================================ */}
                    {/* 1. COMPTE */}
                    {/* ============================================ */}

                    <Card
                        style={{
                            background: isDarkMode ? "#1f1f1f" : "#ffffff",
                            marginBottom: "24px",
                            border: `1px solid ${isDarkMode ? "#434343" : "#d9d9d9"}`,
                        }}
                    >
                        <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px" }}>
                            👤 Compte
                        </h3>

                        <Form.Item
                            label="Nom d'utilisateur"
                            name="login"
                            rules={[
                                { required: true, message: "Requis" },
                                { min: 3, message: "Au moins 3 caractères" },
                            ]}
                        >
                            <Input
                                prefix={<UserOutlined />}
                                placeholder="tonlogin"
                                disabled={saving}
                                size="large"
                            />
                        </Form.Item>
                    </Card>

                    {/* ============================================ */}
                    {/* 2. PASSWORD */}
                    {/* ============================================ */}

                    <Card
                        style={{
                            background: isDarkMode ? "#1f1f1f" : "#ffffff",
                            marginBottom: "24px",
                            border: `1px solid ${isDarkMode ? "#434343" : "#d9d9d9"}`,
                        }}
                    >
                        <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px" }}>
                            🔐 Mot de passe
                        </h3>

                        {/* ✅ FIX 2: Alert avec couleur texte pour dark mode */}
                        <Alert
                            message="Laisser vide pour ne pas changer"
                            type="info"
                            style={{
                                marginBottom: "16px",
                                color: isDarkMode ? "#000000" : "undefined",  // ✅ Texte noir en dark mode
                                backgroundColor: isDarkMode ? "#e6f7ff" : "undefined",
                            }}
                            showIcon
                        />

                        <Form.Item
                            label="Nouveau mot de passe"
                            name="password"
                            rules={[
                                {
                                    min: 6,
                                    message: "Au moins 6 caractères",
                                },
                            ]}
                        >
                            <Input.Password
                                prefix={<LockOutlined />}
                                placeholder="••••••"
                                disabled={saving}
                                size="large"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Confirmer le mot de passe"
                            name="password_confirm"
                        >
                            <Input.Password
                                prefix={<LockOutlined />}
                                placeholder="••••••"
                                disabled={saving}
                                size="large"
                            />
                        </Form.Item>
                    </Card>

                    {/* ============================================ */}
                    {/* 3. DISCORD */}
                    {/* ============================================ */}

                    <Card
                        style={{
                            background: isDarkMode ? "#1f1f1f" : "#ffffff",
                            marginBottom: "24px",
                            border: `1px solid ${isDarkMode ? "#434343" : "#d9d9d9"}`,
                        }}
                    >
                        <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px" }}>
                            💬 Discord Notifications
                        </h3>

                        {/* ✅ FIX 2: Alert avec couleur texte pour dark mode */}
                        <Alert
                            message="Reçois des notifications Discord en DM privé quand une carte change.
                            Il faut que tu fasses partie du discord altered-checker"
                            type="info"
                            style={{
                                marginBottom: "24px",
                                color: isDarkMode ? "#000000" : "undefined",
                                backgroundColor: isDarkMode ? "#e6f7ff" : "undefined",
                            }}
                            showIcon
                        />

                        {/* OAuth Button */}
                        <div style={{ marginBottom: "24px" }}>
                            <Button
                                type="primary"
                                size="large"
                                block
                                icon={<LinkOutlined />}
                                onClick={handleDiscordOAuth}
                                loading={saving}
                            >
                                {settings.discord_user_id
                                    ? "🔄 Reconnecter Discord"
                                    : "🔗 Connecter Discord"}
                            </Button>
                            <p style={{ fontSize: "12px", opacity: 0.6, marginTop: "8px" }}>
                                (Recommandé) 1 clic pour conecter automatiquement
                            </p>
                        </div>

                        <Divider style={{ margin: "16px 0" }}>ou</Divider>

                        {/* Manual Input */}
                        <Form.Item
                            label="Ou entrer manuellement ton Discord User ID"
                            name="discord_user_id"
                        >
                            <Input
                                placeholder="123456789"
                                disabled={saving}
                                size="large"
                                suffix={<LinkOutlined />}
                            />
                        </Form.Item>

                        {/* Help Text */}
                        <div
                            style={{
                                background: isDarkMode ? "#262626" : "#f5f5f5",
                                padding: "12px 16px",
                                borderRadius: "8px",
                                fontSize: "12px",
                                opacity: 0.7,
                                marginBottom: "16px",
                                border: `1px solid ${isDarkMode ? "#434343" : "#e0e0e0"}`,
                            }}
                        >
                            <p style={{ margin: "0 0 8px", fontWeight: "600" }}>
                                📖 Comment obtenir ton Discord User ID :
                            </p>
                            <ol style={{ margin: "0", paddingLeft: "20px" }}>
                                <li>Ouvre Discord</li>
                                <li>Settings → Advanced → Developer Mode ON</li>
                                <li>Clic-droit sur ton profil</li>
                                <li>"Copy User ID"</li>
                            </ol>
                        </div>

                        {/* Discord Status */}
                        {settings.discord_user_id && (
                            <div
                                style={{
                                    padding: "12px 16px",
                                    background: isDarkMode ? "#1a3a1a" : "#f6ffed",
                                    borderRadius: "8px",
                                    borderLeft: "4px solid #52c41a",
                                    marginBottom: "16px",
                                }}
                            >
                                <p style={{ margin: "0 0 12px", fontSize: "13px" }}>
                                    ✅ <strong>Connecté</strong> (ID: {settings.discord_user_id})
                                </p>
                                <Button
                                    size="small"
                                    danger
                                    onClick={handleRemoveDiscord}
                                    loading={saving}
                                >
                                    Déconnecter
                                </Button>
                            </div>
                        )}
                    </Card>

                    {/* ============================================ */}
                    {/* SAVE BUTTON */}
                    {/* ============================================ */}

                    <div style={{ marginBottom: "32px" }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            block
                            icon={<SaveOutlined />}
                            loading={saving}
                        >
                            💾 Sauvegarder les modifications
                        </Button>
                    </div>

                    {/* ============================================ */}
                    {/* DELETE ACCOUNT */}
                    {/* ============================================ */}

                    <Card
                        style={{
                            background: isDarkMode ? "#2a1f1f" : "#fff2f0",
                            borderColor: "#ff4d4f",
                            border: `1px solid ${isDarkMode ? "#ff4d4f" : "#ffccc7"}`,
                            marginBottom: "24px",
                        }}
                    >
                        <h3
                            style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                color: "#ff4d4f",
                                marginBottom: "12px",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                            }}
                        >
                            <WarningOutlined /> Zone Danger
                        </h3>

                        <p style={{ margin: "0 0 16px", fontSize: "14px", opacity: 0.8 }}>
                            Supprimer définitivement ton compte. <strong>Cette action est irréversible.</strong>
                        </p>

                        <Tooltip title="Attention : Supprimera tout (compte, recherches, alertes, favoris)">
                            <Button
                                danger
                                size="large"
                                icon={<DeleteOutlined />}
                                onClick={handleDeleteAccount}
                                loading={saving}
                            >
                                🗑️ Supprimer mon compte
                            </Button>
                        </Tooltip>
                    </Card>
                </Form>
            </div>
        </div>
    );
}